import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	IconComponent,
	createNotification,
	CustomIconButton,
	icon,
	CustomPopOver,
	CustomDialog,
	CustomButton,
	CustomTooltip,
	VersionComparisonPanel,
} from "../../../../../../../../common/components";
import { ApiService, CollabService, ReqViewService } from "../../../../../../../../api";
import { setHasVersioningUpdate, updateNode } from "../../../../../../slice/pdf/pdf-slice";
import { setHoveredBlock, setSelectedSearch } from "../../../../../../slice/document/document-slice";
import { translate, hasPermission, hasCompanyPermission, Permissions } from "../../../../../../../../common/providers";
import styles from "./RequirementVersionOverlay.module.css";
import { dangerousSetInnerHTML } from "../../../../../../../../common/utils";
import { checkHardInformation } from "../../../../../../utils/utils";
import { DOCUMENT_STATUSES, VERSION_STATUSES } from "../../../../../../constants/constants";
import { SOURCE_TYPES } from "../../../../../../../../common/constants";

function VersionText({ text }) {
	return dangerousSetInnerHTML({ text });
}
export default function RequirementVersionOverlay({
	hoveredWarning,
	metadata,
	priority,
	onClose,
	setHoveredWarning,
	onSetPriority,
}) {
	const [open, setOpen] = useState(false);
	const [mode] = useState("reclassify");
	const isNew = metadata?.infoVersionStatus === VERSION_STATUSES.NEW;
	const isModified = metadata?.infoVersionStatus === VERSION_STATUSES.MODIFIED;
	const isModifiedMinor = metadata?.infoVersionStatus === VERSION_STATUSES.MODIFIED_MINOR;
	const dispatch = useDispatch();
	const [openConfirmation, setOpenConfirmation] = useState(false);
	const [comparisonOpen, setComparisonOpen] = useState(false);
	const [newContentText, setNewContentText] = useState("");
	const hasVersioningUpdate = useSelector(({ srPdf }) => srPdf.hasVersioningUpdate);
	const showReclassifyButton = useRef(false);
	const cancelTokenSourceRef = useRef(null);
	const selectedMode = useSelector(({ srDocument }) => srDocument.mode);
	const isSelecting = useSelector(({ srDocument }) => srDocument.isSelecting);
	const docId = useSelector(({ srDocument }) => srDocument.documentId);
	const documentInfo = useSelector(({ srDocument }) => srDocument);
	const previousVersionId = useSelector(({ srDocument }) => srDocument.previousVersionId);
	const previousVersionName = useSelector(({ srDocument }) => srDocument.previousVersionName);
	const isArchived = useSelector(({ srDocument }) => srDocument.status === DOCUMENT_STATUSES.ARCHIVED);
	const popUpTitle = useMemo(
		() => ({
			reclassify: translate("smart-review.version-confirm-dialog.reclassify.title"),
			setIdentical: translate("smart-review.version-confirm-dialog.identical.title"),
			removeNew: translate("smart-review.version-confirm-dialog.identical.title"),
		}),
		[]
	);
	const popUpText = useMemo(
		() => ({
			reclassify: translate("smart-review.version-confirm-dialog.reclassify.text"),
			setIdentical: translate("smart-review.version-confirm-dialog.identical.text"),
			removeNew: translate("smart-review.version-confirm-dialog.remove-new.text"),
		}),
		[]
	);
	const isNewContent = useMemo(() => metadata?.infoVersionStatus === VERSION_STATUSES.NEW, [metadata]);
	useEffect(() => {
		cancelTokenSourceRef.current = ApiService.getCancelTokenSource();
		showReclassifyButton.current =
			hasPermission(Permissions.PROJECT_LEADER, Permissions.PROJECT_MANAGER) &&
			hasCompanyPermission(Permissions.PROJECT_LEADER);
		return () => {
			ApiService.cancelTokens(cancelTokenSourceRef.current);
		};
	}, []);
	useEffect(() => {
		if (hoveredWarning !== metadata.informationId) {
			setOpen(false);
		}
	}, [hoveredWarning, metadata]);

	useEffect(() => {
		const handleScroll = () => {
			if (open) {
				setOpen(false);
			}
		};

		window.addEventListener("scroll", handleScroll, true);

		return () => {
			window.removeEventListener("scroll", handleScroll, true);
		};
	}, [open]);
	const getVersioningData = (informationId) => {
		CollabService.getVersioningData({ informationId }, cancelTokenSourceRef.current.token)
			.then((data) => {
				dispatch(
					updateNode({
						mode: selectedMode,
						pageConditions: { page: metadata.pageStart },
						informationId: metadata.informationId,
						updates: [{ property: "versionDifference", value: data }],
					})
				);
			})
			.catch((err) => console.error(err));
	};
	useEffect(
		() => {
			if (hasVersioningUpdate && open) {
				getVersioningData(metadata.informationId);
				dispatch(setHasVersioningUpdate(false));
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[dispatch, hasVersioningUpdate, open]
	);
	useEffect(() => {
		if (isNewContent) {
			CollabService.getStringContent({ infoId: metadata.informationId }, cancelTokenSourceRef.current.token)
				.then((data) => {
					setNewContentText(data.value);
				})
				.catch(console.error);
		}
	}, [isNewContent, metadata]);
	const handleMouseEnter = () => {
		if (typeof onClose === "function") {
			onClose();
		}
		if (!isSelecting) {
			if (priority) {
				onSetPriority("single");
			}
			dispatch(setHoveredBlock(metadata));
			setHoveredWarning(metadata.informationId);
			setOpen(true);
			if (!metadata?.versionDifference) {
				getVersioningData(metadata.informationId);
			}
		}
	};
	const handleClose = (e) => {
		e?.stopPropagation();
		setOpen(false);
	};
	useEffect(() => {
		if (priority && priority !== "single") {
			setOpen(false);
		}
	}, [priority, onClose]);
	const handleRedirect = (e) => {
		e?.stopPropagation();
		handleClose();
		setComparisonOpen(true);
	};

	const handleCloseComparison = () => {
		setComparisonOpen(false);
	};
	const handleMouseLeave = () => {
		dispatch(setHoveredBlock(null));
	};

	const handleOpenConfirmation = () => setOpenConfirmation((prev) => !prev);

	const handleSubmit = () => {
		handleOpenConfirmation();
		const infoVersionStatus = mode === "reclassify" ? VERSION_STATUSES.NEW : VERSION_STATUSES.IDENTICAL;
		ReqViewService[mode]({ infoId: metadata.informationId }, cancelTokenSourceRef.current.token)
			.then(() => {
				createNotification({
					type: "success",
					message: translate("smart-review.version-notification.success-message"),
					title: translate("smart-review.version-notification.success-title"),
				});
				dispatch(
					updateNode({
						mode: selectedMode,
						pageConditions: { page: metadata.pageStart },
						informationId: metadata.informationId,
						updates: [{ property: "infoVersionStatus", value: infoVersionStatus }],
					})
				);
			})
			.catch((err) => {
				console.error(err);
			});
	};

	const diffText = {
		text: metadata?.versionDifference?.diff,
		title: translate(
			isNew ? "smart-review.version-icon.version-new-content" : "smart-review.version-icon.version-difference"
		),
	};

	const setInfoVersionStatusText = () => {
		if (isModifiedMinor) {
			return translate("smart-review.version-info-icon.modified_minor");
		}
		if (isModified) {
			return translate("smart-review.version-info-icon.modified");
		}
		return translate("smart-review.version-info-icon.new");
	};
	const handleOpenSidePanel = () => {
		if (!isArchived) {
			const isRequirement = checkHardInformation(metadata);
			handleClose();
			dispatch(
				setSelectedSearch({
					infoId: metadata.informationId,
					pageStart: metadata.pageStart,
					x1: metadata.rectangle.x1,
					x2: metadata.rectangle.x2,
					y1: metadata.rectangle.y1,
					y2: metadata.rectangle.y2,
					openSidePanel: true,
					source: SOURCE_TYPES.VERSIONING,
					isRequirement,
				})
			);
		}
	};
	return (
		<>
			<div
				key={metadata.informationId}
				className={styles.versionIcon}
				role="presentation"
				onClick={handleOpenSidePanel}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
			>
				<CustomPopOver
					content={
						<>
							<div className={styles.content}>
								<div className={styles.modal__header}>
									<div className={styles.modal__title}>
										<span>{diffText.title}</span>
										<CustomTooltip title={setInfoVersionStatusText()}>
											<span>
												<IconComponent
													className={styles.infoIcon}
													icon={icon.faInfoCircle}
													size="sm"
												/>
											</span>
										</CustomTooltip>
									</div>
									{!isNewContent && (
										<CustomIconButton
											className={styles.compare__btn}
											icon={icon.faCopy}
											size="xs"
											tooltip={translate("smart-review.version-icon.redirection")}
											variant="outlined"
											onClick={handleRedirect}
										/>
									)}
								</div>
								<div className={styles.content__body}>
									{isNewContent ? (
										<div className={styles.newContent}>{newContentText}</div>
									) : (
										<VersionText text={diffText?.text || ""} />
									)}
								</div>
								{!isArchived && (
									<div className={styles.openPanelBtn}>
										<CustomButton
											color="secondary"
											size="sm"
											variant="outlined"
											onClick={handleOpenSidePanel}
										>
											{translate("smart-review.version-modal.open-panel")}
										</CustomButton>
									</div>
								)}
							</div>
						</>
					}
					open={open}
					paperClassName={styles.paperRoot}
					placement="right-start"
					onClose={handleClose}
				>
					<span role="presentation" onClick={handleOpenSidePanel}>
						<IconComponent
							color={
								(isNew && "var(--blue-500)") ||
								(isModified && "var(--red-500)") ||
								(isModifiedMinor && "var(--color-keppel)")
							}
							icon={isNew ? icon.faPlusCircle : icon.faExclamationTriangle}
							size="lg"
						/>
					</span>
				</CustomPopOver>
			</div>
			<CustomDialog
				open={openConfirmation}
				subTitle={popUpText[mode]}
				title={popUpTitle[mode]}
				onClose={handleOpenConfirmation}
				onSubmit={handleSubmit}
			/>
			<VersionComparisonPanel
				enableLeftApiCall
				currentDocId={docId}
				currentDocName={documentInfo?.documentName}
				enableRightApiCall={false}
				initialLeftInfoId={metadata?.informationId}
				initialLeftPage={metadata?.pageStart || 1}
				initialRightInfoId={isNew ? null : metadata?.versionDifference?.oldInformationId}
				initialRightPage={isNew ? metadata?.pageStart || 1 : metadata?.versionDifference?.oldPageStart || 1}
				open={comparisonOpen}
				previousDocId={previousVersionId}
				previousDocName={previousVersionName || documentInfo?.previousVersionName}
				projectId={documentInfo?.projectId}
				onClose={handleCloseComparison}
			/>
		</>
	);
}
